'use client';

import React, { useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface InteractiveGridProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: number;
  height?: number;
  lineColor?: string;
  className?: string;
  revealImageSrc?: string; // New prop
  showHint?: boolean;
}

type GameState = 'idle' | 'playing' | 'complete' | 'gameover';
type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
type Point = { x: number; y: number };

const COLORS = [
  // ... existing colors can be removed if not used, or kept for fallback
  '#ffffff',
];
// ...

export const InteractiveGrid = ({
  width = 20,
  height = 20,
  lineColor = 'rgba(0, 0, 0, 0.08)',
  className,
  revealImageSrc,
  showHint = true,
  ...props
}: InteractiveGridProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [gameState, setGameState] = useState<GameState>('idle');
  const requestRef = useRef<number>(0);
  const revealImageRef = useRef<HTMLImageElement | null>(null);

  // Load image if provided
  useEffect(() => {
    if (revealImageSrc) {
      const img = new Image();
      img.src = revealImageSrc;
      img.onload = () => {
        revealImageRef.current = img;
      };
    }
  }, [revealImageSrc]);

  // ... rest of state ...
  const activeCells = useRef<
    Array<{ x: number; y: number; age: number; color: string }>
  >([]);

  // Snake Game State
  const snake = useRef<Point[]>([{ x: 5, y: 5 }]);
  const direction = useRef<Direction>('RIGHT');
  const nextDirection = useRef<Direction>('RIGHT');
  const food = useRef<Point>({ x: 10, y: 10 });
  const score = useRef(0);
  const lastUpdate = useRef(0);
  const GAME_SPEED = 100; // ms

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      if (containerRef.current && canvas) {
        const { width: w, height: h } =
          containerRef.current.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        ctx.scale(dpr, dpr);
      }
    };

    const spawnFood = (cols: number, rows: number) => {
      let newFood: Point;
      do {
        newFood = {
          x: Math.floor(Math.random() * cols),
          y: Math.floor(Math.random() * rows),
        };
      } while (
        snake.current.some((s) => s.x === newFood.x && s.y === newFood.y)
      );
      food.current = newFood;
    };

    const updateGame = (timestamp: number, w: number, h: number) => {
      if (timestamp - lastUpdate.current < GAME_SPEED) return;
      lastUpdate.current = timestamp;

      const cols = Math.ceil(w / width);
      const rows = Math.ceil(h / height);

      direction.current = nextDirection.current;
      const head = { ...snake.current[0] };

      switch (direction.current) {
        case 'UP':
          head.y--;
          break;
        case 'DOWN':
          head.y++;
          break;
        case 'LEFT':
          head.x--;
          break;
        case 'RIGHT':
          head.x++;
          break;
      }

      // Check collision with walls
      if (head.x < 0 || head.x >= cols || head.y < 0 || head.y >= rows) {
        setGameState('gameover');
        return;
      }

      // Check collision with self
      if (snake.current.some((s) => s.x === head.x && s.y === head.y)) {
        setGameState('gameover');
        return;
      }

      snake.current.unshift(head);

      // Check food
      if (head.x === food.current.x && head.y === food.current.y) {
        score.current += 10;
        spawnFood(cols, rows);
      } else {
        snake.current.pop();
      }
    };

    const draw = (timestamp: number) => {
      const w = canvas.width / (window.devicePixelRatio || 1);
      const h = canvas.height / (window.devicePixelRatio || 1);

      ctx.clearRect(0, 0, w, h);

      // Draw Grid
      const majorGridStep = 5;

      // Minor lines
      ctx.beginPath();
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 1;
      for (let x = 0; x <= w; x += width) {
        if (x % (width * majorGridStep) === 0) continue;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
      }
      for (let y = 0; y <= h; y += height) {
        if (y % (height * majorGridStep) === 0) continue;
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();

      // Major lines
      ctx.beginPath();
      // Even softer major lines
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.04)';
      ctx.lineWidth = 1;
      for (let x = 0; x <= w; x += width * majorGridStep) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
      }
      for (let y = 0; y <= h; y += height * majorGridStep) {
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();

      // Update and draw active cells
      const newCells = activeCells.current
        .map((cell) => ({ ...cell, age: cell.age - 0.5 })) // Slow down fade out
        .filter((cell) => cell.age > 0);

      activeCells.current = newCells;

      if (gameState === 'idle') {
        // Interactive Mode
        // We want to "reveal" the background image by erasing the white overlay.
        // However, this canvas is sitting ON TOP of the image.
        // The image has opacity-40 (so it's washed out by the white background behind it?).
        // Wait, the structure is:
        // 1. Background Image (opacity-40)
        // 2. InteractiveGrid Canvas (transparent)
        // 3. White Overlay (gradient) on top? No, gradient is separate.

        // Actually, the user wants to "reveal the full bg without overlay".
        // The "overlay" that makes the BG faint is just the opacity on the image itself.
        // We can't easily change the image opacity under a specific cell from a canvas on top.

        // Alternative trick:
        // Draw the "full opacity" version of the image INSIDE the canvas cell?
        // Or simply: draw a "Clear" rect if there was an overlay on top?
        // But the overlay is the image opacity itself.

        // BEST APPROACH:
        // 1. We need the "full color" background image available to the canvas (or load it).
        // 2. When hovering, we draw that chunk of the image onto the canvas at full opacity.

        // Let's try to load the image into an HTMLImageElement and draw it.
        // Since we know the URL is fixed for this specific hero, we can hardcode or pass it.

        // Let's update the component to accept an `revealImage` prop.

        activeCells.current.forEach((cell) => {
          // We will handle the image drawing in the main draw loop if the image is loaded
          // For now, let's just keep the loop structure.
          const opacity = cell.age / 50;
          ctx.globalAlpha = opacity;

          if (revealImageRef.current) {
            // Calculate source coordinates based on canvas size vs image size
            // The image is "object-cover", so we need to mimic that math.

            // Simplification: Assume the image fills the canvas exactly for now (since we use object-cover and fill in Next.js)
            // But canvas W/H might not match Image natural W/H aspect ratio.
            // We need to calculate the "cover" mapping.

            const img = revealImageRef.current;
            const canvasAspect = w / h;
            const imgAspect = img.naturalWidth / img.naturalHeight;

            let sWidth, sHeight, sX, sY;

            if (canvasAspect > imgAspect) {
              // Canvas is wider than image -> Image fits width, cropped height?
              // No, object-cover means it covers the container.
              // If canvas is wider, we scale image to match width, and crop top/bottom?
              // Wait: object-cover: "The replaced content is sized to maintain its aspect ratio while filling the element's entire content box."

              sWidth = img.naturalWidth;
              sHeight = img.naturalWidth / canvasAspect;
              sX = 0;
              sY = (img.naturalHeight - sHeight) / 2;
            } else {
              // Canvas is taller than image -> Image fits height, crop left/right
              sHeight = img.naturalHeight;
              sWidth = img.naturalHeight * canvasAspect;
              sY = 0;
              sX = (img.naturalWidth - sWidth) / 2;
            }

            const dX = cell.x * width;
            const dY = cell.y * height;
            const dW = width;
            const dH = height;

            // Scale factor from Dest to Source
            const scaleX = sWidth / w;
            const scaleY = sHeight / h;

            const sxCell = sX + dX * scaleX;
            const syCell = sY + dY * scaleY;
            const swCell = dW * scaleX;
            const shCell = dH * scaleY;

            ctx.drawImage(img, sxCell, syCell, swCell, shCell, dX, dY, dW, dH);
          } else {
            // Fallback to color if image not loaded
            ctx.fillStyle = cell.color;
            ctx.fillRect(cell.x * width, cell.y * height, width, height);
          }
        });
        ctx.globalAlpha = 1.0;
      } else {
        // Game Mode
        updateGame(timestamp, w, h);

        if (gameState === 'playing') {
          // Draw Food
          ctx.fillStyle = '#ef4444'; // Red
          ctx.fillRect(
            food.current.x * width,
            food.current.y * height,
            width,
            height,
          );

          // Draw Snake
          snake.current.forEach((segment, i) => {
            ctx.fillStyle = i === 0 ? '#22c55e' : '#4ade80'; // Green head, lighter body
            ctx.fillRect(segment.x * width, segment.y * height, width, height);
          });
        } else if (gameState === 'gameover') {
          // Draw grayed out snake
          snake.current.forEach((segment) => {
            ctx.fillStyle = '#9ca3af';
            ctx.fillRect(segment.x * width, segment.y * height, width, height);
          });

          // Minimal reset hint
          const dpr = window.devicePixelRatio || 1;
          ctx.fillStyle = '#6b7280'; // Gray-500
          ctx.font = `${12 * dpr}px sans-serif`;
          ctx.textAlign = 'right';
          ctx.fillText(
            `Score: ${score.current} • Double click to restart`,
            w - 20 * dpr,
            h - 20 * dpr,
          );
        }
      }

      requestRef.current = requestAnimationFrame(draw);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (gameState !== 'idle' || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const cellX = Math.floor(x / width);
      const cellY = Math.floor(y / height);

      // Add main cursor cell
      const addCell = (cX: number, cY: number, initialAge: number) => {
        const exists = activeCells.current.some(
          (c) => c.x === cX && c.y === cY && c.age > initialAge - 5,
        );
        if (!exists) {
          const randomColor = COLORS[Math.floor(Math.random() * COLORS.length)];
          activeCells.current.push({
            x: cX,
            y: cY,
            age: initialAge,
            color: randomColor,
          });
        }
      };

      addCell(cellX, cellY, 50);

      // Add neighbors with lower intensity (lower age) to create a "glow" effect
      // Increased radius to 2 cells
      const neighbors = [
        // Direct neighbors (Radius 1)
        { dx: 0, dy: -1, age: 35 },
        { dx: 0, dy: 1, age: 35 },
        { dx: -1, dy: 0, age: 35 },
        { dx: 1, dy: 0, age: 35 },
        // Diagonals (Radius 1)
        { dx: -1, dy: -1, age: 30 },
        { dx: 1, dy: -1, age: 30 },
        { dx: -1, dy: 1, age: 30 },
        { dx: 1, dy: 1, age: 30 },
        // Radius 2 (fainter)
        { dx: 0, dy: -2, age: 20 },
        { dx: 0, dy: 2, age: 20 },
        { dx: -2, dy: 0, age: 20 },
        { dx: 2, dy: 0, age: 20 },
      ];

      neighbors.forEach(({ dx, dy, age }) => {
        // Randomize intensity slightly for organic feel
        const randomAge = age + (Math.random() * 10 - 5);
        addCell(cellX + dx, cellY + dy, randomAge);
      });
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (gameState !== 'playing') return;
      switch (e.key) {
        case 'ArrowUp':
          if (direction.current !== 'DOWN') nextDirection.current = 'UP';
          break;
        case 'ArrowDown':
          if (direction.current !== 'UP') nextDirection.current = 'DOWN';
          break;
        case 'ArrowLeft':
          if (direction.current !== 'RIGHT') nextDirection.current = 'LEFT';
          break;
        case 'ArrowRight':
          if (direction.current !== 'LEFT') nextDirection.current = 'RIGHT';
          break;
      }
    };

    window.addEventListener('resize', resize);
    window.addEventListener('keydown', handleKeyDown);
    if (containerRef.current) {
      containerRef.current.addEventListener('mousemove', handleMouseMove);
    }

    resize();
    requestRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('keydown', handleKeyDown);
      if (containerRef.current) {
        containerRef.current.removeEventListener('mousemove', handleMouseMove);
      }
      cancelAnimationFrame(requestRef.current);
    };
  }, [width, height, lineColor, gameState]);

  const handleDoubleClick = () => {
    if (gameState === 'idle' || gameState === 'gameover') {
      setGameState('playing');
      snake.current = [{ x: 5, y: 5 }];
      direction.current = 'RIGHT';
      nextDirection.current = 'RIGHT';
      score.current = 0;
      // Focus the container or window to capture keys
      window.focus();
    } else {
      setGameState('idle');
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn('absolute inset-0 h-full w-full', className)}
      onDoubleClick={handleDoubleClick}
      {...props}
    >
      <canvas ref={canvasRef} className="block" />
      {gameState === 'idle' && showHint && (
        <div className="pointer-events-none absolute right-4 bottom-4 text-xs text-neutral-400 opacity-50 select-none">
          Double click to play
        </div>
      )}
    </div>
  );
};
